import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api, type ConflictRow } from '../lib/api.js';
import { StatCard } from '../components/StatCard.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useSortable } from '../hooks/useSortable.js';
import { SortableTh } from '../components/SortableTh.js';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';

type FilterTab = 'open' | 'resolved' | 'all';

function severityColor(severity: string): { bg: string; text: string; border: string } {
  if (severity === 'critical' || severity === 'error') {
    return { bg: 'var(--accent-red)22', text: 'var(--accent-red)', border: 'var(--accent-red)55' };
  }
  if (severity === 'warning' || severity === 'warn') {
    return { bg: 'var(--accent-orange)22', text: 'var(--accent-orange)', border: 'var(--accent-orange)55' };
  }
  return { bg: 'var(--accent-yellow)22', text: 'var(--accent-yellow)', border: 'var(--accent-yellow)55' };
}

function ConfirmOverwrite({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-text-dimmed mr-1">Confirm?</span>
      <button
        type="button"
        onClick={onConfirm}
        className="rounded px-2 py-0.5 text-xs font-semibold"
        style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)', border: '1px solid var(--accent-red)55' }}
      >
        Yes, overwrite
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded px-2 py-0.5 text-xs font-semibold text-text-secondary hover:text-text-primary"
      >
        Cancel
      </button>
    </div>
  );
}

function ConflictActions({ conflict }: { conflict: ConflictRow }) {
  const qc = useQueryClient();
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const resolve = useMutation({
    mutationFn: (action: 'overwrite' | 'skip' | 'flag') =>
      api.resolveConflict(conflict.id, { action }),
    onSuccess: (_, action) => {
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
      toast.success(`Conflict ${action === 'skip' ? 'skipped' : action === 'flag' ? 'flagged' : 'overwritten'}`);
    },
    onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  if (conflict.status === 'resolved') {
    return (
      <span className="text-xs text-text-dimmed">Resolved</span>
    );
  }

  if (confirmOverwrite) {
    return (
      <ConfirmOverwrite
        onConfirm={() => { setConfirmOverwrite(false); resolve.mutate('overwrite'); }}
        onCancel={() => setConfirmOverwrite(false)}
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={resolve.isPending}
        onClick={() => resolve.mutate('skip')}
        className="rounded px-2 py-0.5 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
        style={{ background: 'var(--accent-yellow)22', color: 'var(--accent-yellow)', border: '1px solid var(--accent-yellow)55' }}
      >
        Skip
      </button>
      <button
        type="button"
        disabled={resolve.isPending}
        onClick={() => setConfirmOverwrite(true)}
        className="rounded px-2 py-0.5 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
        style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)', border: '1px solid var(--accent-red)55' }}
      >
        Overwrite
      </button>
      <button
        type="button"
        disabled={resolve.isPending}
        onClick={() => resolve.mutate('flag')}
        className="rounded px-2 py-0.5 text-xs font-semibold text-text-dimmed hover:text-text-secondary transition-colors disabled:opacity-40"
        style={{ border: '1px solid var(--border)' }}
      >
        Flag
      </button>
    </div>
  );
}

export function ConflictsPage() {
  const navigate = useNavigate();
  usePageTitle('Conflicts');
  const [tab, setTab] = useState<FilterTab>('open');
  const { sortKey, sortDir, toggle, sort } = useSortable<'sessionTitle' | 'kind' | 'severity' | 'status' | 'createdAt'>('createdAt', 'desc');

  const { data, isLoading } = useQuery({
    queryKey: ['conflicts'],
    queryFn: () => api.listConflicts(),
  });

  const conflicts: ConflictRow[] = data?.conflicts ?? [];
  const open = conflicts.filter((c) => c.status === 'open');
  const resolved = conflicts.filter((c) => c.status === 'resolved');

  // "this week" = created in the last 7 days
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const thisWeek = conflicts.filter((c) => new Date(c.createdAt).getTime() > oneWeekAgo).length;
  const affectedSessions = new Set(open.map((c) => c.sessionId)).size;

  const tabFiltered = tab === 'open' ? open : tab === 'resolved' ? resolved : conflicts;
  const filtered = sort(tabFiltered, (c) => c[sortKey as keyof ConflictRow] as string ?? '');

  const TABS: { key: FilterTab; label: string; count: number }[] = [
    { key: 'open', label: 'Open', count: open.length },
    { key: 'resolved', label: 'Resolved', count: resolved.length },
    { key: 'all', label: 'All', count: conflicts.length },
  ];

  return (
    <div className="flex h-full flex-col">
      <PageHeader eyebrow="Data Integrity" title="Conflicts" />

      <section className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Open" value={String(open.length)} icon="⚠" colorIndex={2} />
          <StatCard label="Resolved" value={String(resolved.length)} icon="✓" colorIndex={1} />
          <StatCard label="This week" value={String(thisWeek)} icon="◷" colorIndex={0} />
          <StatCard label="Affected sessions" value={String(affectedSessions)} icon="⧉" colorIndex={3} />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold capitalize tracking-wide transition-all duration-150"
              style={
                tab === t.key
                  ? { background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(13,207,184,0.35)' }
                  : { background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }
              }
            >
              {t.label} · {t.count}
            </button>
          ))}
        </div>

        {/* Conflicts table */}
        <div
          className="glass rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="text-sm font-semibold text-text-primary">
              {tab === 'open' ? 'Open Conflicts' : tab === 'resolved' ? 'Resolved Conflicts' : 'All Conflicts'}
            </div>
            <div className="text-xs text-text-dimmed">{filtered.length} {tab === 'open' ? 'unresolved' : 'total'}</div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-sm text-text-dimmed">Loading…</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={tab === 'open' ? '✅' : '🔍'}
              title={tab === 'open' ? 'No open conflicts' : 'No conflicts found'}
              description={tab === 'open' ? 'All imports are clean. No data integrity issues detected.' : 'Nothing matches this filter.'}
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <SortableTh label="Session" sortKey="sessionTitle" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Kind" sortKey="kind" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Severity" sortKey="severity" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Context</th>
                  <SortableTh label="Status" sortKey="status" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const sc = severityColor(c.severity);
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100 cursor-pointer"
                      onClick={() => void navigate({ to: '/sessions/$id', params: { id: c.sessionId } })}
                    >
                      <td className="px-5 py-3">
                        <div className="font-semibold text-text-primary">{c.sessionTitle ?? '—'}</div>
                        <div className="font-mono text-xs text-text-dimmed">{c.sessionId.slice(0, 8)}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="rounded-md border px-2 py-0.5 font-mono text-xs"
                          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                        >
                          {c.kind}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                          style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
                        >
                          {c.severity}
                        </span>
                      </td>
                      <td className="px-5 py-3 max-w-[200px]">
                        <div className="font-mono text-xs truncate text-text-secondary">
                          {Object.entries(c.context ?? {}).slice(0, 2).map(([k, v]) => (
                            <div key={k}><span style={{ color: 'var(--accent-orange)' }}>{k}</span>: {String(v)}</div>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                          style={
                            c.status === 'resolved'
                              ? { background: 'var(--accent-green)22', color: 'var(--accent-green)', border: '1px solid var(--accent-green)55' }
                              : { background: 'var(--accent-yellow)22', color: 'var(--accent-yellow)', border: '1px solid var(--accent-yellow)55' }
                          }
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <ConflictActions conflict={c} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
